import { useCallback, useEffect, useState } from 'react';
import type { AuthUser } from '@pantheon/shared';

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

interface UseAuthOptions {
  apiUrl: string;
  onTokenReceived?: (token: string) => void;
}

const STORAGE_KEY = 'pantheon_auth_token';
const STORAGE_USER_KEY = 'pantheon_auth_user';

export function useAuth({ apiUrl, onTokenReceived }: UseAuthOptions) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    loading: true,
    error: null,
  });

  // Restore session from localStorage on mount
  // Try to refresh an expired token
  const tryRefresh = useCallback(async (token: string, user: string): Promise<boolean> => {
    try {
      const baseUrl = apiUrl.replace(/\/$/, '');
      const res = await fetch(`${baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const body = await res.json();
      if (res.ok && body.success && body.data?.token) {
        localStorage.setItem(STORAGE_KEY, body.data.token);
        setState({
          user: JSON.parse(user),
          token: body.data.token,
          loading: false,
          error: null,
        });
        return true;
      }
    } catch {}
    return false;
  }, [apiUrl]);

  // Restore session on mount — try refresh if expired
  useEffect(() => {
    const savedToken = localStorage.getItem(STORAGE_KEY);
    const savedUser = localStorage.getItem(STORAGE_USER_KEY);

    if (savedToken && savedUser) {
      try {
        const payload = JSON.parse(atob(savedToken.split('.')[1]));
        if (payload.exp * 1000 > Date.now()) {
          // Token still valid
          setState({
            user: JSON.parse(savedUser),
            token: savedToken,
            loading: false,
            error: null,
          });

          // Schedule refresh 1 hour before expiry
          const msUntilRefresh = (payload.exp * 1000) - Date.now() - (60 * 60 * 1000);
          if (msUntilRefresh > 0) {
            setTimeout(() => tryRefresh(savedToken, savedUser), msUntilRefresh);
          }
          return;
        }

        // Token expired — try to refresh
        tryRefresh(savedToken, savedUser).then((success) => {
          if (!success) {
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(STORAGE_USER_KEY);
            setState((prev) => ({ ...prev, loading: false }));
          }
        });
        return;
      } catch {
        // Invalid token
      }
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_USER_KEY);
    }

    setState((prev) => ({ ...prev, loading: false }));
  }, [tryRefresh]);

  // Initiate Google OAuth redirect
  const login = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const baseUrl = apiUrl.replace(/\/$/, '');
      const res = await fetch(`${baseUrl}/auth/config`);
      const { data } = await res.json();

      const redirectUri = `${window.location.origin}/auth/callback`;
      const params = new URLSearchParams({
        client_id: data.googleClientId,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: 'openid email profile https://www.googleapis.com/auth/drive.readonly https://www.googleapis.com/auth/drive.file',
        hd: data.allowedDomain, // Restricts to Workspace domain
        access_type: 'offline',
        prompt: 'consent',
      });

      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
    } catch (err) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to start login',
      }));
    }
  }, [apiUrl]);

  // Handle the OAuth callback (exchange code for token)
  const handleCallback = useCallback(
    async (code: string) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const baseUrl = apiUrl.replace(/\/$/, '');
        const redirectUri = `${window.location.origin}/auth/callback`;

        const res = await fetch(`${baseUrl}/auth/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, redirectUri }),
        });

        const body = await res.json();

        if (!res.ok || !body.success) {
          throw new Error(body.error?.message ?? 'Token exchange failed');
        }

        const { token, user } = body.data;

        localStorage.setItem(STORAGE_KEY, token);
        localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(user));

        setState({ user, token, loading: false, error: null });
        onTokenReceived?.(token);
      } catch (err) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: err instanceof Error ? err.message : 'Authentication failed',
        }));
      }
    },
    [apiUrl, onTokenReceived],
  );

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_USER_KEY);
    window.location.href = '/';
  }, []);

  return {
    user: state.user,
    token: state.token,
    loading: state.loading,
    error: state.error,
    isAuthenticated: !!state.token,
    login,
    logout,
    handleCallback,
  };
}
